"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  isSocialConnectMessage,
  SOCIAL_CONNECT_CHANNEL,
} from "../const/socialConnect";
import { SocialProviderType } from "../types/models";
import { useSocialProviderAccounts } from "./useSocialProviderAccounts";

// How long a connect keeps the list polling when its tab never reports
// back. OAuth runs in another tab and can take a while (2FA, account
// pickers); a later return is still caught by the callback page's message
// and the query's window-focus refetch.
const CONNECT_POLL_WINDOW_MS = 5 * 60 * 1000;

// Starts a social account connect and keeps the accounts list fresh until
// it lands. The OAuth flow runs in another tab or window, and coming back
// doesn't always fire a focus or visibility event, so the list polls while a
// connect started here may be in flight, and stops as soon as the callback
// page reports back (see const/socialConnect.ts). Never longer: the page can
// sit open for hours, and a refetch can reach every provider's profile API
// (YouTube quota included).
export const useConnectAccountPoll = () => {
  const {
    authorize,
    readAccounts: { refetch },
  } = useSocialProviderAccounts();
  // Bumped by each connect, so another one restarts the poll window; null
  // when none is in flight.
  const [pendingConnect, setPendingConnect] = useState<number | null>(null);
  // Latest connect started, so an earlier one failing to start doesn't end
  // the poll of a later one.
  const connectSeq = useRef(0);
  // The opener can get one notify twice (BroadcastChannel and
  // window.opener): act on it once.
  const lastMessageId = useRef<string | null>(null);
  // The capacity dialog may close while its connect-link request (or the
  // deferred window.open) is pending. Closing must not launch OAuth later.
  const pendingAuthorization = useRef<AbortController | null>(null);
  useEffect(() => () => { pendingAuthorization.current?.abort(); }, []);

  useEffect(() => {
    if (pendingConnect === null) return;
    const interval = setInterval(() => {
      // Let a slow response finish rather than have each tick cancel it.
      refetch({ cancelRefetch: false });
    }, 3000);
    const timeout = setTimeout(() => {
      setPendingConnect(null);
    }, CONNECT_POLL_WINDOW_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [pendingConnect, refetch]);

  // The callback page's verdict, connected or failed: the flow is over
  // either way, and the list may have changed (a failed callback can still
  // have saved the token). Listened for whenever mounted, not just while
  // polling, so a connect finishing after the window is caught too.
  useEffect(() => {
    const handleMessage = (data: unknown) => {
      if (!isSocialConnectMessage(data)) return;
      if (data.id === lastMessageId.current) return;
      lastMessageId.current = data.id;
      refetch();
      setPendingConnect(null);
    };

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(SOCIAL_CONNECT_CHANNEL);
      channel.onmessage = (event) => handleMessage(event.data);
    } catch {
      // No BroadcastChannel: the callback page also posts to window.opener.
    }
    const handleWindowMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      handleMessage(event.data);
    };
    window.addEventListener("message", handleWindowMessage);

    return () => {
      channel?.close();
      window.removeEventListener("message", handleWindowMessage);
    };
  }, [refetch]);

  const stopConnecting = useCallback(() => {
    pendingAuthorization.current?.abort();
    setPendingConnect(null);
  }, []);

  const connect = (provider: SocialProviderType, accountId?: string, options?: { via: "postbridge"; popup?: Window }) => {
    const seq = ++connectSeq.current;
    pendingAuthorization.current?.abort();
    const controller = options ? new AbortController() : null;
    pendingAuthorization.current = controller;
    setPendingConnect((count) => (count ?? 0) + 1);
    const authorization = options
      ? authorize(provider, accountId, { ...options, signal: controller!.signal })
      : authorize(provider, accountId);
    authorization.catch((error) => {
      if (controller?.signal.aborted) return;
      if (connectSeq.current === seq) setPendingConnect(null);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Couldn't start the connection. Please try again.",
      );
    });
  };

  return { connect, stopConnecting, isConnecting: pendingConnect !== null };
};
